/* Own theme scripts: gallery, sliders, videos, bundles, sticky bar, small helpers. */
(function () {
  'use strict';

  const OWN = (window.OwnTheme = window.OwnTheme || {});

  OWN.formatMoney = function (cents, format) {
    const fmt = format || (window.OwnTheme && window.OwnTheme.moneyFormat) || '${{amount}}';
    const value = Number(cents || 0) / 100;
    const withDelims = (n, decimals, thousands, decimal) => {
      const fixed = n.toFixed(decimals);
      const parts = fixed.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return decimals ? parts.join(decimal) : parts[0];
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (m, key) => {
      switch (key) {
        case 'amount_no_decimals': return withDelims(value, 0, ',', '.');
        case 'amount_with_comma_separator': return withDelims(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelims(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return withDelims(value, 2, "'", '.');
        case 'amount_with_space_separator': return withDelims(value, 2, ' ', ',');
        case 'amount_no_decimals_with_space_separator': return withDelims(value, 0, ' ', ',');
        case 'amount_with_period_and_space_separator': return withDelims(value, 2, ' ', '.');
        default: return withDelims(value, 2, ',', '.');
      }
    });
  };

  const onScrollEnd = (el, cb) => {
    let t;
    el.addEventListener('scroll', () => { clearTimeout(t); t = setTimeout(cb, 80); }, { passive: true });
  };

  /* ---------- generic scroll slider ---------- */
  class OwnSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      if (!this.track) return;
      this.slides = Array.from(this.track.children);
      this.dots = this.querySelector('[data-dots]');
      if (this.dots && !this.dots.children.length && this.slides.length > 1) {
        this.slides.forEach((s, i) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'own-slider__dot' + (i === 0 ? ' is-active' : '');
          b.dataset.go = i;
          b.setAttribute('aria-label', 'Slide ' + (i + 1));
          this.dots.appendChild(b);
        });
      }
      this.addEventListener('click', (e) => {
        const go = e.target.closest('[data-go]');
        if (go && this.contains(go)) return this.goTo(Number(go.dataset.go));
        if (e.target.closest('[data-prev]')) return this.step(-1);
        if (e.target.closest('[data-next]')) return this.step(1);
      });
      onScrollEnd(this.track, () => this.sync());
      const sec = Number(this.dataset.autoplay || 0);
      if (sec > 0 && this.slides.length > 1) {
        this.timer = setInterval(() => { if (!this.matches(':hover')) this.step(1, true); }, sec * 1000);
      }
    }
    disconnectedCallback() { clearInterval(this.timer); }
    perMove() {
      const desk = window.matchMedia('(min-width: 750px)').matches;
      return Number((desk ? this.dataset.perMoveD : this.dataset.perMoveM) || 1);
    }
    index() {
      const left = this.track.scrollLeft;
      const base = this.slides[0] ? this.slides[0].offsetLeft : 0;
      let best = 0, dist = Infinity;
      this.slides.forEach((s, i) => { const d = Math.abs(s.offsetLeft - base - left); if (d < dist) { dist = d; best = i; } });
      return best;
    }
    goTo(i) {
      const n = this.slides.length;
      if (!n) return;
      i = Math.max(0, Math.min(n - 1, i));
      const target = this.slides[i];
      this.track.scrollTo({ left: target.offsetLeft - this.slides[0].offsetLeft, behavior: 'smooth' });
      this.current = i;
      this.markDots(i);
    }
    step(dir, wrap) {
      const n = this.slides.length;
      let i = this.index() + dir * this.perMove();
      const atEnd = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 4;
      if (dir > 0 && atEnd) i = (wrap || this.dataset.loop) ? 0 : n - 1;
      if (i < 0) i = this.dataset.loop ? n - 1 : 0;
      this.goTo(i);
    }
    sync() { this.markDots(this.index()); }
    markDots(i) {
      if (!this.dots) return;
      Array.from(this.dots.children).forEach((d, k) => d.classList.toggle('is-active', k === i));
    }
  }
  if (!customElements.get('own-slider')) customElements.define('own-slider', OwnSlider);

  /* ---------- product gallery ---------- */
  class OwnGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.slides = Array.from(this.track.children);
      this.addEventListener('click', (e) => {
        const go = e.target.closest('[data-go]');
        if (go) return this.goTo(Number(go.dataset.go));
        if (e.target.closest('[data-prev]')) return this.goTo(this.index() - 1);
        if (e.target.closest('[data-next]')) return this.goTo(this.index() + 1);
        const zoom = e.target.closest('[data-zoom]');
        if (zoom) {
          const all = Array.from(this.querySelectorAll('[data-zoom]'));
          return ownLightbox.open(all.map((z) => ({ src: z.dataset.full, alt: z.dataset.alt || '' })), all.indexOf(zoom));
        }
      });
      onScrollEnd(this.track, () => this.mark(this.index()));
      const first = this.slides.findIndex((s) => s.classList.contains('is-active'));
      if (first > 0) requestAnimationFrame(() => this.goTo(first, true));
    }
    index() {
      const w = this.slides[0] ? this.slides[0].getBoundingClientRect().width : 1;
      return Math.round(this.track.scrollLeft / (w || 1));
    }
    goTo(i, instant) {
      const n = this.slides.length;
      if (!n) return;
      if (i < 0) i = n - 1;
      if (i >= n) i = 0;
      this.track.scrollTo({ left: this.slides[i].offsetLeft - this.slides[0].offsetLeft, behavior: instant ? 'auto' : 'smooth' });
      this.mark(i);
    }
    goToMedia(mediaId) {
      const i = this.slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
      if (i >= 0) this.goTo(i);
    }
    mark(i) {
      this.querySelectorAll('.own-gallery__dot').forEach((d, k) => d.classList.toggle('is-active', k === i));
      this.querySelectorAll('.own-gallery__thumb').forEach((d, k) => d.classList.toggle('is-active', k === i));
      const thumb = this.querySelectorAll('.own-gallery__thumb')[i];
      if (thumb && thumb.parentElement) thumb.parentElement.scrollTo({ left: thumb.offsetLeft - thumb.parentElement.offsetLeft - 20, behavior: 'smooth' });
      this.slides.forEach((s, k) => {
        const v = s.querySelector('video');
        if (v && k !== i && !v.paused) v.pause();
      });
    }
  }
  if (!customElements.get('own-gallery')) customElements.define('own-gallery', OwnGallery);

  /* Full-screen image viewer for the product gallery (built once, on first use). */
  const ownLightbox = {
    el: null,
    build() {
      const d = document.createElement('dialog');
      d.className = 'own-lightbox';
      d.setAttribute('aria-label', 'Image viewer');
      d.innerHTML = '<div class="own-lightbox__track" data-lb-track></div>' +
        '<button type="button" class="own-lightbox__close" data-lb-close aria-label="Close"><span class="own-msym" aria-hidden="true">close</span></button>' +
        '<button type="button" class="own-lightbox__nav own-lightbox__nav--prev" data-lb-step="-1" aria-label="Previous"><span class="own-msym" aria-hidden="true">chevron_left</span></button>' +
        '<button type="button" class="own-lightbox__nav own-lightbox__nav--next" data-lb-step="1" aria-label="Next"><span class="own-msym" aria-hidden="true">chevron_right</span></button>';
      d.addEventListener('click', (e) => {
        if (e.target.closest('[data-lb-close]') || e.target.classList.contains('own-lightbox__item')) return d.close();
        const step = e.target.closest('[data-lb-step]');
        if (step) this.show(this.current() + Number(step.dataset.lbStep), false);
      });
      d.addEventListener('close', () => { document.documentElement.classList.remove('own-lightbox-open'); });
      document.body.appendChild(d);
      this.el = d;
      this.track = d.querySelector('[data-lb-track]');
    },
    current() {
      const w = this.track.clientWidth || 1;
      return Math.round(this.track.scrollLeft / w);
    },
    show(i, instant) {
      const n = this.track.children.length;
      if (!n) return;
      i = (i + n) % n;
      this.track.scrollTo({ left: i * this.track.clientWidth, behavior: instant ? 'auto' : 'smooth' });
    },
    open(images, start) {
      if (!this.el) this.build();
      this.track.innerHTML = '';
      images.forEach((img) => {
        const item = document.createElement('div');
        item.className = 'own-lightbox__item';
        const im = document.createElement('img');
        im.src = img.src; im.alt = img.alt; im.loading = 'lazy'; im.decoding = 'async';
        item.appendChild(im);
        this.track.appendChild(item);
      });
      this.el.classList.toggle('own-lightbox--single', images.length < 2);
      document.documentElement.classList.add('own-lightbox-open');
      this.el.showModal();
      requestAnimationFrame(() => this.show(Math.max(0, start), true));
    },
  };

  /* ---------- videos ---------- */
  class OwnVideo extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      if (!this.video) return;
      const auto = this.dataset.autoplay === 'true';
      if (auto) {
        this.video.muted = true;
        this.video.setAttribute('muted', '');
        this.video.playsInline = true;
        const io = new IntersectionObserver((entries) => {
          entries.forEach((en) => { if (en.isIntersecting) this.play(); else this.video.pause(); });
        }, { threshold: 0.25 });
        io.observe(this);
      }
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-sound]')) {
          this.video.muted = !this.video.muted;
          this.classList.toggle('is-unmuted', !this.video.muted);
          if (this.video.paused) this.play();
          return;
        }
        if (e.target.closest('[data-play]') || e.target === this.video) {
          if (this.video.paused) { if (e.target.closest('[data-play]')) { this.video.muted = false; this.classList.add('is-unmuted'); } this.play(); }
          else if (!auto) this.video.pause();
        }
      });
      this.video.addEventListener('play', () => this.classList.add('is-playing'));
      this.video.addEventListener('pause', () => this.classList.remove('is-playing'));
      const tl = this.querySelector('[data-timeline]');
      if (tl) this.video.addEventListener('timeupdate', () => { tl.style.width = (this.video.currentTime / (this.video.duration || 1)) * 100 + '%'; });
    }
    play() { const p = this.video.play(); if (p && p.catch) p.catch(() => {}); }
  }
  if (!customElements.get('own-video')) customElements.define('own-video', OwnVideo);

  /* ---------- cart helpers ---------- */
  OWN.getCart = () => document.querySelector('cart-drawer') || document.querySelector('cart-notification');

  OWN.addItems = async function (items, opts = {}) {
    const cart = OWN.getCart();
    const body = { items };
    if (cart && typeof cart.getSectionsToRender === 'function') {
      body.sections = cart.getSectionsToRender().map((s) => s.id);
      body.sections_url = window.location.pathname;
    }
    const res = await fetch((window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : '/cart/add') + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (data.status) throw data;
    if (opts.checkout) { window.location.href = '/checkout'; return data; }
    if (!cart) { window.location.href = (window.routes && window.routes.cart_url) || '/cart'; return data; }
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'own-bundles' });
    if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
    const scroller = cart.querySelector('.drawer__inner');
    const top = scroller && cart.classList.contains('active') ? scroller.scrollTop : 0;
    cart.renderContents(data);
    const again = cart.querySelector('.drawer__inner');
    if (again && top) again.scrollTop = top;
    return data;
  };

  /* ---------- bundle cards ---------- */
  class OwnBundles extends HTMLElement {
    connectedCallback() {
      this.formId = this.dataset.formId;
      this.cards = Array.from(this.querySelectorAll('.own-bundle'));
      this.addEventListener('change', (e) => {
        if (e.target.matches('.own-bundle__input')) this.select(e.target.closest('.own-bundle'));
        if (e.target.matches('[data-unit]')) this.onUnitChange(e.target);
      });
      this.addEventListener('click', (e) => {
        const card = e.target.closest('.own-bundle');
        if (!card || e.target.closest('select')) return;
        const input = card.querySelector('.own-bundle__input');
        if (input && !input.checked) { input.checked = true; this.select(card); }
      });
      this._onSubmit = this.onSubmit.bind(this);
      this._onVariant = (e) => this.onVariantChange(e.detail);
      document.addEventListener('submit', this._onSubmit, true);
      document.addEventListener('own:variant-change', this._onVariant);
      this.select(this.selected(), true);
    }
    disconnectedCallback() {
      document.removeEventListener('submit', this._onSubmit, true);
      document.removeEventListener('own:variant-change', this._onVariant);
    }
    selected() { return this.cards.find((c) => c.querySelector('.own-bundle__input').checked) || this.cards[0]; }
    select(card, silent) {
      if (!card) return;
      this.cards.forEach((c) => c.classList.toggle('is-selected', c === card));
      const price = card.querySelector('.own-bundle__price[data-price]');
      const compare = card.querySelector('s[data-compare]');
      document.querySelectorAll('[data-sticky-price]').forEach((el) => {
        const now = el.querySelector('.own-sticky__now');
        const was = el.querySelector('.own-sticky__was');
        if (now && price) now.innerHTML = price.innerHTML;
        if (was) was.remove();
        if (compare) { const s = document.createElement('s'); s.className = 'own-sticky__was'; s.innerHTML = compare.innerHTML; el.appendChild(s); }
      });
      if (!silent) document.dispatchEvent(new CustomEvent('own:bundle-change', { detail: { qty: Number(card.dataset.qty) } }));
    }
    onUnitChange(select) {
      const opt = select.selectedOptions[0];
      if (!opt) return;
      const gallery = document.querySelector('own-gallery');
      const v = this.variantById(Number(select.value));
      if (gallery && v && v.featured_media) gallery.goToMedia(v.featured_media.id);
    }
    variantById(id) {
      if (!this.variants) {
        const json = document.querySelector('[data-product-json]');
        this.variants = json ? JSON.parse(json.textContent).variants : [];
      }
      return this.variants.find((v) => v.id === id);
    }
    onVariantChange(variant) {
      if (!variant) return;
      this.querySelectorAll('[data-unit]').forEach((s) => { s.value = String(variant.id); });
      if (this.dataset.updatePrices !== 'true') return;
      this.cards.forEach((card) => {
        const qty = Number(card.dataset.qty);
        const total = variant.price * qty;
        let off = Number(card.dataset.fixed || 0);
        const pct = Number(card.dataset.pct || 0);
        if (pct > 0) off += Math.floor((total * pct) / 100);
        const price = Math.max(0, total - off);
        const el = card.querySelector('.own-bundle__price[data-price]');
        if (el) el.textContent = OWN.formatMoney(price, this.dataset.moneyFormat);
      });
    }
    items() {
      const card = this.selected();
      const qty = Number(card.dataset.qty || 1);
      const selects = Array.from(card.querySelectorAll('[data-unit]'));
      const counts = new Map();
      if (selects.length) {
        selects.forEach((s) => counts.set(s.value, (counts.get(s.value) || 0) + 1));
      } else {
        const form = document.getElementById(this.formId);
        const id = form && form.querySelector('[name="id"]') ? form.querySelector('[name="id"]').value : null;
        if (id) counts.set(id, qty);
      }
      return Array.from(counts, ([id, quantity]) => ({ id: Number(id), quantity }));
    }
    async onSubmit(e) {
      const form = e.target;
      if (!form || !this.isConnected || form.getAttribute("id") !== this.formId) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      await this.addToCart(form);
    }
    async addToCart(form, opts = {}) {
      form = form || document.getElementById(this.formId);
      if (!form || this.busy) return;
      this.busy = true;
      const wrapper = form.closest('product-form') || form.parentElement;
      const btn = form.querySelector('[type="submit"]');
      const spinner = form.querySelector('.loading-overlay__spinner');
      const errBox = wrapper ? wrapper.querySelector('.product-form__error-message-wrapper') : null;
      if (btn) { btn.setAttribute('aria-disabled', 'true'); btn.classList.add('loading'); }
      if (spinner) spinner.classList.remove('hidden');
      if (errBox) errBox.hidden = true;
      const skip = wrapper && wrapper.dataset.skipCart === 'true';
      try {
        await OWN.addItems(this.items(), { checkout: skip || opts.checkout });
      } catch (err) {
        if (errBox) { errBox.hidden = false; errBox.querySelector('.product-form__error-message').textContent = err.description || err.message || 'Error'; }
      } finally {
        if (btn) { btn.removeAttribute('aria-disabled'); btn.classList.remove('loading'); }
        if (spinner) spinner.classList.add('hidden');
        this.busy = false;
      }
    }
  }
  if (!customElements.get('own-bundles')) customElements.define('own-bundles', OwnBundles);

  /* buy-now buttons and plain forms without bundles */
  document.addEventListener('click', async (e) => {
    const buyNow = e.target.closest('[data-buy-now]');
    if (!buyNow) return;
    e.preventDefault();
    const form = buyNow.closest('form');
    if (!form || buyNow.getAttribute('aria-disabled') === 'true') return;
    const bundles = document.querySelector(`own-bundles[data-form-id="${form.getAttribute("id")}"]`);
    if (bundles) return bundles.addToCart(form, { checkout: true });
    const idInput = form.querySelector('[name="id"]');
    if (!idInput) return;
    const q = document.querySelector(`[form="${form.getAttribute("id")}"][name="quantity"]`);
    buyNow.setAttribute('aria-disabled', 'true');
    buyNow.classList.add('loading');
    try {
      await OWN.addItems([{ id: Number(idInput.value), quantity: Number(q ? q.value : 1) }], { checkout: true });
    } catch (err) {
      const box = form.closest('product-form') && form.closest('product-form').querySelector('.product-form__error-message-wrapper');
      if (box) { box.hidden = false; box.querySelector('.product-form__error-message').textContent = err.description || err.message || 'Error'; }
    } finally {
      buyNow.removeAttribute('aria-disabled');
      buyNow.classList.remove('loading');
    }
  });

  /* ---------- sticky add to cart ---------- */
  class OwnStickyAtc extends HTMLElement {
    connectedCallback() {
      this.form = document.getElementById(this.dataset.formId);
      const main = this.form ? this.form.querySelector('[type="submit"]') : null;
      const always = this.dataset.when === 'always';
      if (always) this.show(true);
      else if (main) {
        const io = new IntersectionObserver(([en]) => {
          const below = en.boundingClientRect.top < 0;
          this.show(!en.isIntersecting && below);
        });
        io.observe(main);
      }
      const picker = this.querySelector('[data-sticky-variant]');
      if (picker) {
        picker.addEventListener('change', () => {
          const json = document.querySelector('[data-product-json]');
          const variants = json ? (JSON.parse(json.textContent).variants || []) : [];
          const v = variants.find((x) => String(x.id) === picker.value);
          if (!v) return;
          const input = this.form ? this.form.querySelector('[name="id"]') : null;
          if (input) input.value = v.id;
          const main = document.querySelector('own-variant-picker');
          if (main) {
            main.querySelectorAll('[data-option-index]').forEach((fs, i) => {
              const sel = fs.querySelector('select');
              if (sel) sel.value = v.options[i];
              else { const r = fs.querySelector(`input[value="${CSS.escape(v.options[i])}"]`); if (r) r.checked = true; }
            });
            main.update();
          } else {
            document.dispatchEvent(new CustomEvent('own:variant-change', { detail: v }));
          }
        });
        document.addEventListener('own:variant-change', (e) => { if (e.detail && picker.value !== String(e.detail.id)) picker.value = String(e.detail.id); });
      }
      const btn = this.querySelector('[data-sticky-button]');
      if (btn) btn.addEventListener('click', () => {
        const fn = this.dataset.function || '';
        if (fn.indexOf('scroll') === 0) {
          let target = null;
          if (fn === 'scroll_#ProductInfo-id') target = document.querySelector('[id^="ProductInfo-"]');
          else if (fn === 'scroll_#variant-selects-id') target = document.querySelector('own-variant-picker');
          target = target || document.querySelector('own-bundles') || this.form;
          if (target) target.scrollIntoView({ behavior: 'smooth', block: fn === 'scroll_#ProductInfo-id' ? 'start' : 'center' });
          return;
        }
        if (!this.form) return;
        if (typeof this.form.requestSubmit === 'function') this.form.requestSubmit();
        else this.form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      });
    }
    show(on) {
      this.classList.toggle('is-visible', on);
      this.setAttribute('aria-hidden', on ? 'false' : 'true');
      document.body.classList.toggle('own-sticky-on', on);
      const bar = this.classList.contains('own-sticky') ? this : this.querySelector('.own-sticky');
      document.documentElement.style.setProperty('--own-sticky-h', on && bar ? bar.offsetHeight + 'px' : '0px');
    }
  }
  if (!customElements.get('own-sticky-atc')) customElements.define('own-sticky-atc', OwnStickyAtc);

  /* ---------- variant picker ---------- */
  class OwnVariantPicker extends HTMLElement {
    connectedCallback() {
      this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
      this.form = document.getElementById(this.dataset.formId);
      const mainBtn = this.form ? this.form.querySelector('[type="submit"]') : null;
      this.priceInLabel = !!(mainBtn && mainBtn.querySelector('[data-buy-price]'));
      const sticky = document.querySelector('own-sticky-atc [data-sticky-button]');
      this.stickyLabel = sticky && !sticky.disabled ? sticky.textContent.trim() : '';
      this.addEventListener('change', () => this.update());
    }
    current() {
      return Array.from(this.querySelectorAll('[data-option-index]')).map((fs) => {
        const sel = fs.querySelector('select');
        if (sel) return sel.value;
        const r = fs.querySelector('input:checked');
        return r ? r.value : null;
      });
    }
    update() {
      const opts = this.current();
      const v = this.variants.find((x) => x.options.every((o, i) => o === opts[i]));
      this.querySelectorAll('[data-option-index]').forEach((fs, i) => {
        const legend = fs.querySelector('.own-variants__label');
        if (legend && legend.dataset.template) legend.textContent = legend.dataset.template.replace('[selected]', opts[i]);
      });
      const strings = window.variantStrings || {};
      const btn = this.form ? this.form.querySelector('[type="submit"]') : null;
      const setBtn = (text, disabled) => {
        [btn, document.querySelector('own-sticky-atc [data-sticky-button]')].forEach((b) => {
          if (!b) return;
          b.disabled = disabled;
          const label = b.querySelector('span') || b;
          if (disabled) label.textContent = text;
        });
      };
      if (!v) { setBtn(strings.unavailable || 'Unavailable', true); return; }
      if (this.form) {
        const input = this.form.querySelector('[name="id"]');
        if (input) input.value = v.id;
      }
      if (!v.available) setBtn(strings.soldOut || 'Sold out', true);
      else if (btn && btn.disabled) {
        btn.disabled = false;
        const label = btn.querySelector('span');
        if (label) label.innerHTML = (strings.addToCart || 'Add to cart') + (this.priceInLabel ? ' · <span data-buy-price></span>' : '');
        const sb = document.querySelector('own-sticky-atc [data-sticky-button]');
        if (sb) { sb.disabled = false; sb.textContent = this.stickyLabel || strings.addToCart || 'Add to cart'; }
      }
      document.querySelectorAll('[data-buy-price]').forEach((el) => { el.textContent = OWN.formatMoney(v.price); });
      document.querySelectorAll('[data-sticky-price] .own-sticky__now').forEach((el) => { if (!document.querySelector('own-bundles')) el.textContent = OWN.formatMoney(v.price); });
      const sec = this.closest('section');
      const now = sec && sec.querySelector('[data-price-now]');
      const was = sec && sec.querySelector('[data-price-was]');
      if (now) now.textContent = OWN.formatMoney(v.price);
      if (was) { was.textContent = OWN.formatMoney(v.compare_at_price || 0); was.classList.toggle('hidden', !(v.compare_at_price > v.price)); }
      const gallery = document.querySelector('own-gallery');
      if (gallery && v.featured_media) gallery.goToMedia(v.featured_media.id);
      const url = new URL(window.location.href);
      url.searchParams.set('variant', v.id);
      window.history.replaceState({}, '', url.toString());
      document.dispatchEvent(new CustomEvent('own:variant-change', { detail: v }));
    }
  }
  if (!customElements.get('own-variant-picker')) customElements.define('own-variant-picker', OwnVariantPicker);

  /* ---------- delivery timeline dates ---------- */
  class OwnCheckpoints extends HTMLElement {
    connectedCallback() {
      const days = (this.dataset.days || '').split(',').map((s) => s.trim());
      const months = (this.dataset.months || '').split(',').map((s) => s.trim());
      const fmt = this.dataset.format;
      const label = (offset) => {
        const d = new Date();
        d.setDate(d.getDate() + offset);
        const wd = days[(d.getDay() + 6) % 7] || '';
        const mo = months[d.getMonth()] || '';
        if (fmt === 'dd_mm') return `${d.getDate()} ${mo}`;
        if (fmt === 'day_dd_mm') return `${wd}, ${d.getDate()} ${mo}`;
        return `${mo} ${d.getDate()}`;
      };
      this.querySelectorAll('[data-template]').forEach((el) => {
        const tpl = el.dataset.template;
        const tmp = document.createElement('textarea');
        tmp.innerHTML = tpl;
        el.innerHTML = tmp.value.replace('[start_date]', label(Number(el.dataset.min))).replace('[end_date]', label(Number(el.dataset.max)));
      });
    }
  }
  if (!customElements.get('own-checkpoints')) customElements.define('own-checkpoints', OwnCheckpoints);

  /* ---------- quick add (section buttons) ---------- */
  class OwnQuickAdd extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', async (e) => {
        if (!e.target.closest('button')) return;
        e.preventDefault();
        await OWN.addItems([{ id: Number(this.dataset.variant), quantity: 1 }], { checkout: this.dataset.skipCart === 'true' });
      });
    }
  }
  if (!customElements.get('own-quick-add')) customElements.define('own-quick-add', OwnQuickAdd);

  /* ---------- scroll to top ---------- */
  class OwnTop extends HTMLElement {
    connectedCallback() {
      const btn = this.querySelector('button');
      const after = Number(this.dataset.after || 400);
      const toggle = () => btn.classList.toggle('is-visible', window.scrollY > after);
      window.addEventListener('scroll', toggle, { passive: true });
      toggle();
      btn.addEventListener('click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));
    }
  }
  if (!customElements.get('own-top')) customElements.define('own-top', OwnTop);

  /* ---------- cart reservation timer (survives drawer re-renders) ---------- */
  setInterval(() => {
    const els = document.querySelectorAll('[data-cart-timer]');
    if (!els.length) return;
    let end = 0;
    try { end = Number(sessionStorage.getItem('own-cart-timer-end') || 0); } catch (e) {}
    const now = Date.now();
    if (!end || end < now) {
      end = now + Number(els[0].dataset.seconds || 300) * 1000;
      try { sessionStorage.setItem('own-cart-timer-end', String(end)); } catch (e) {}
    }
    const left = Math.max(0, Math.round((end - now) / 1000));
    const txt = String(Math.floor(left / 60)).padStart(2, '0') + ':' + String(left % 60).padStart(2, '0');
    els.forEach((el) => { el.textContent = txt; });
  }, 1000);

  /* ---------- cart discount code field ---------- */
  class OwnCartDiscount extends HTMLElement {
    connectedCallback() {
      const input = this.querySelector('input');
      const btn = this.querySelector('button');
      input.addEventListener('change', (e) => e.stopPropagation());
      input.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); btn.click(); } });
      btn.addEventListener('click', async () => {
        const code = input.value.trim();
        if (!code) { input.setCustomValidity(this.dataset.error || 'Required'); input.reportValidity(); return; }
        input.setCustomValidity('');
        if (btn.classList.contains('loading')) return;
        btn.classList.add('loading');
        try {
          await fetch('/discount/' + encodeURIComponent(code), { credentials: 'same-origin' });
          if (!this.closest('cart-drawer')) { window.location.reload(); return; }
          const cart = OWN.getCart();
          if (cart && cart.getSectionsToRender) {
            const ids = cart.getSectionsToRender().map((x) => x.id).join(',');
            const res = await fetch(window.location.pathname + '?sections=' + ids);
            const sections = await res.json();
            cart.renderContents({ sections });
          }
        } catch (err) {
          input.setCustomValidity(this.dataset.error || 'Error');
          input.reportValidity();
        } finally {
          btn.classList.remove('loading');
        }
      });
    }
  }
  if (!customElements.get('own-cart-discount')) customElements.define('own-cart-discount', OwnCartDiscount);

  /* ---------- cart add-on products ---------- */
  OWN.updateCart = async function (updates) {
    const cart = OWN.getCart();
    const body = { updates };
    if (cart && typeof cart.getSectionsToRender === 'function') {
      body.sections = cart.getSectionsToRender().map((s) => s.id);
      body.sections_url = window.location.pathname;
    }
    const res = await fetch((window.routes && window.routes.cart_update_url ? window.routes.cart_update_url : '/cart/update') + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (data.status) throw data;
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'own-upsells' });
    if (cart && data.sections) {
      const scroller = cart.querySelector('.drawer__inner');
      const top = scroller ? scroller.scrollTop : 0;
      cart.classList.toggle('is-empty', !data.item_count);
      cart.renderContents(data);
      const again = cart.querySelector('.drawer__inner');
      if (again) again.scrollTop = top;
    }
    return data;
  };

  class OwnUpsells extends HTMLElement {
    connectedCallback() {
      this.querySelectorAll('[data-upsell]').forEach((card) => this.setup(card));
    }
    setup(card) {
      const btn = card.querySelector('[data-upsell-btn]');
      const picker = card.querySelector('[data-upsell-select]');
      const tapCard = card.classList.contains('own-upsell--tap');
      const onTap = (e) => {
        if (e.target.closest('select, a, option')) return;
        if (!tapCard && !e.target.closest('[data-upsell-btn]')) return;
        e.preventDefault();
        this.toggle(card, btn, picker);
      };
      card.addEventListener('click', onTap);
      if (picker && picker.tagName === 'SELECT') {
        picker.addEventListener('change', (e) => { e.stopPropagation(); this.pick(card, btn, picker); });
      }
      if (card.dataset.preselected === 'true' && !card.classList.contains('is-on')) {
        const key = 'own-upsell-auto-' + card.dataset.productId;
        let done = false;
        try { done = sessionStorage.getItem(key) === '1'; sessionStorage.setItem(key, '1'); } catch (err) { done = true; }
        if (!done) this.toggle(card, btn, picker);
      }
    }
    lines(card) {
      return (card.dataset.lines || '').split(',').filter(Boolean);
    }
    async run(btn, job) {
      if (btn.classList.contains('is-busy')) return;
      btn.classList.add('is-busy');
      const spin = btn.querySelector('.loading-overlay__spinner');
      if (spin) spin.classList.remove('hidden');
      try { await job(); } catch (err) {
        console.error(err);
        const card = btn.closest('[data-upsell]');
        if (card && err && err.description) { let m = card.querySelector('.own-upsell__error'); if (!m) { m = document.createElement('p'); m.className = 'own-upsell__error'; card.querySelector('.own-upsell__info').appendChild(m); } m.textContent = err.description; }
      }
      finally {
        btn.classList.remove('is-busy');
        if (spin) spin.classList.add('hidden');
      }
    }
    toggle(card, btn, picker) {
      const on = card.classList.contains('is-on');
      return this.run(btn, async () => {
        if (on) {
          const updates = {};
          this.lines(card).forEach((id) => { updates[id] = 0; });
          await OWN.updateCart(updates);
        } else {
          const id = Number(picker && picker.value);
          if (!id) return;
          await OWN.addItems([{ id, quantity: 1 }]);
        }
      });
    }
    pick(card, btn, picker) {
      const opt = picker.options[picker.selectedIndex];
      if (!opt) return;
      const img = card.querySelector('[data-upsell-img]');
      if (img && opt.dataset.img) { img.src = opt.dataset.img; img.removeAttribute('srcset'); }
      const box = card.querySelector('[data-upsell-price]');
      if (box && this.dataset.updatePrices === 'true') {
        const base = Number(opt.dataset.price || 0);
        const pct = Number(box.dataset.pct || 0);
        const fixed = Number(box.dataset.fixed || 0);
        const now = Math.max(0, base - Math.floor(base * pct / 100) - fixed);
        let was = Number(opt.dataset.compare || 0);
        if (now < base && was < base) was = base;
        const nowEl = box.querySelector('[data-upsell-now]');
        if (nowEl) nowEl.textContent = OWN.formatMoney(now);
        const wasEl = box.querySelector('[data-upsell-was]');
        if (wasEl) { wasEl.textContent = OWN.formatMoney(was); wasEl.hidden = !(was > now); }
      }
      if (card.classList.contains('is-on')) {
        const updates = {};
        this.lines(card).forEach((id) => { updates[id] = 0; });
        updates[picker.value] = 1;
        this.run(btn, () => OWN.updateCart(updates));
      }
    }
  }
  if (!customElements.get('own-upsells')) customElements.define('own-upsells', OwnUpsells);
})();
